const SESSION_TOKEN_KEY = 'siyak_access_token';
const PDF_MAGIC = '%PDF';
const SNIPPET_LIMIT = 500;

export class ReceiptPreviewPdfError extends Error {
  constructor(message, { kind, status = null, contentType = '', snippet = '' } = {}) {
    super(message);
    this.name = 'ReceiptPreviewPdfError';
    this.kind = kind;
    this.status = status;
    this.contentType = contentType;
    this.snippet = snippet;
  }
}

const getSessionToken = () => {
  try {
    return window.sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch (error) {
    return null;
  }
};

const readResponseSnippet = async (response) => {
  try {
    const text = await response.text();
    return text.slice(0, SNIPPET_LIMIT);
  } catch (error) {
    return '';
  }
};

const readBytesSnippet = (bytes) => {
  try {
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes.slice(0, SNIPPET_LIMIT));
  } catch (error) {
    return '';
  }
};

export const fetchReceiptPreviewPdfBytes = async ({ apiUrl, rentId, signal }) => {
  const headers = new Headers({ Accept: 'application/pdf' });
  const token = getSessionToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response;
  try {
    response = await fetch(`${apiUrl}/api/rent/${rentId}/preview-receipt`, {
      credentials: 'include',
      headers,
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw error;
    }
    console.error('[receipt-preview] fetch failed', error);
    throw new ReceiptPreviewPdfError(
      `Fetch failed: ${error?.message || 'Unable to request receipt preview.'}`,
      { kind: 'fetch-failed' },
    );
  }

  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    const snippet = await readResponseSnippet(response);
    console.error('[receipt-preview] non-OK PDF response', {
      status: response.status,
      contentType,
      snippet,
    });
    throw new ReceiptPreviewPdfError(
      `Fetch failed: receipt preview returned HTTP ${response.status}.`,
      { kind: 'fetch-failed', status: response.status, contentType, snippet },
    );
  }

  if (!contentType.toLowerCase().includes('application/pdf')) {
    const snippet = await readResponseSnippet(response);
    console.error('[receipt-preview] non-PDF response', {
      status: response.status,
      contentType,
      snippet,
    });
    throw new ReceiptPreviewPdfError(
      `Non-PDF response: expected application/pdf but received ${contentType || 'no content type'}.`,
      { kind: 'non-pdf', status: response.status, contentType, snippet },
    );
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  const magic = readBytesSnippet(bytes).slice(0, PDF_MAGIC.length);
  if (magic !== PDF_MAGIC) {
    const snippet = readBytesSnippet(bytes);
    console.error('[receipt-preview] invalid PDF bytes', {
      status: response.status,
      contentType,
      byteLength: bytes.byteLength,
      snippet,
    });
    throw new ReceiptPreviewPdfError(
      'Non-PDF response: response body did not start with a PDF header.',
      { kind: 'non-pdf', status: response.status, contentType, snippet },
    );
  }

  console.debug('[receipt-preview] fetched validated PDF bytes', {
    status: response.status,
    contentType,
    byteLength: bytes.byteLength,
  });

  return { bytes, contentType };
};
